import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Printer, Tag, Settings, Zap, Shield, Headphones, Clock } from "lucide-react";
import { fetchProducts, resolveImage } from "../lib/api";
import { waLink } from "../lib/whatsapp";
import { useSiteSettings } from "../lib/siteSettings";
import ProductCard from "../components/ProductCard";

const AUTOMATION = "https://static.prod-images.emergentagent.com/jobs/1b35fc0a-0293-4ff8-ac55-1a277592b1f0/images/9251f1703331ea0d61a840d98f27f1c315e21c4c66733d00c669c777b262c0e1.png";

const SERVICES = [
  {
    icon: Tag,
    title: "Etiquetas Sob Demanda",
    desc: "Fabricação de etiquetas personalizadas em qualquer formato, material e impressão — feitas para cada operação. Esse é o nosso destaque.",
    featured: true,
  },
  {
    icon: Printer,
    title: "Outsourcing de Impressão",
    desc: "Gestão completa do seu parque de impressão. Equipamentos, suprimentos, manutenção e suporte sob um único contrato.",
  },
  {
    icon: Settings,
    title: "Locação de Impressoras",
    desc: "Térmicas, laser e jato de tinta. Sem investimento inicial, com previsibilidade de custo e tecnologia sempre atualizada.",
  },
];

const DIFFERENTIALS = [
  { icon: Zap, title: "Agilidade", desc: "Atendimento rápido e entregas dentro do prazo." },
  { icon: Shield, title: "Confiabilidade", desc: "Qualidade comprovada em cada equipamento e insumo." },
  { icon: Headphones, title: "Suporte dedicado", desc: "Equipe técnica preparada e em constante evolução." },
  { icon: Clock, title: "Parceria de longo prazo", desc: "Mais que fornecedor — parceiro de crescimento." },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const { settings } = useSiteSettings();
  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => setProducts([]));
  }, []);

  const featured = useMemo(() => {
    // Spotlight Etiquetas first, then Ribbons, then Impressoras
    const order = { Etiquetas: 0, Ribbons: 1, Impressoras: 2 };
    return [...products]
      .sort((a, b) => (order[a.category] ?? 9) - (order[b.category] ?? 9))
      .slice(0, 4);
  }, [products]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0F0F10] text-white noise">
        <div className="absolute inset-0">
          <img src={resolveImage(settings.home_hero_image)} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F0F10] via-[#0F0F10]/85 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-36 lg:py-44">
          <div className="font-mono-sm text-xs tracking-[0.3em] uppercase text-[#E84E34] mb-6">
            // {settings.home_overline}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black leading-[0.95] max-w-4xl">
            {settings.home_title_main}<br />
            <span className="text-[#E84E34]">{settings.home_title_accent}</span>
          </h1>
          <p className="mt-8 text-base md:text-lg text-white/75 max-w-2xl leading-relaxed">
            {settings.home_subtitle}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href={waLink(settings.home_cta_primary_message)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-locar"
              className="inline-flex items-center gap-3 bg-[#E84E34] text-white font-bold px-7 py-4 uppercase text-xs tracking-[0.2em] hover:bg-[#CF412A] transition-colors"
            >
              {settings.home_cta_primary_text} <ArrowUpRight size={18} />
            </a>
            <Link
              to="/produtos"
              data-testid="hero-cta-produtos"
              className="inline-flex items-center gap-3 bg-transparent border-2 border-white text-white font-bold px-7 py-4 uppercase text-xs tracking-[0.2em] hover:bg-white hover:text-[#0F0F10] transition-colors"
            >
              {settings.home_cta_secondary_text}
            </Link>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl border-t border-white/10 pt-10">
            <div>
              <div className="font-display text-3xl md:text-4xl font-black text-[#E84E34]">3+</div>
              <div className="font-mono-sm text-[11px] tracking-[0.15em] uppercase text-white/60 mt-1">Anos de atuação</div>
            </div>
            <div>
              <div className="font-display text-3xl md:text-4xl font-black text-[#E84E34]">100%</div>
              <div className="font-mono-sm text-[11px] tracking-[0.15em] uppercase text-white/60 mt-1">Sob demanda</div>
            </div>
            <div>
              <div className="font-display text-3xl md:text-4xl font-black text-[#E84E34]">B2B</div>
              <div className="font-mono-sm text-[11px] tracking-[0.15em] uppercase text-white/60 mt-1">Foco corporativo</div>
            </div>
            <div>
              <div className="font-display text-3xl md:text-4xl font-black text-[#E84E34]">24h</div>
              <div className="font-mono-sm text-[11px] tracking-[0.15em] uppercase text-white/60 mt-1">Resposta ágil</div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVIÇOS */}
      <section className="py-20 md:py-32 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-5">
              <div className="font-mono-sm text-xs tracking-[0.3em] uppercase text-[#E84E34] mb-4">
                // O QUE FAZEMOS
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight leading-[1.05]">
                Soluções completas em impressão & automação.
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-base md:text-lg text-[#374151] leading-relaxed">
                Unimos Outsourcing de impressão e fabricação de etiquetas personalizadas para entregar
                soluções sob medida — porque cada cliente é único.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
            {SERVICES.map((s, i) => {
              const isFeatured = s.featured;
              const linkTo = s.title.includes("Outsourcing") ? "/outsourcing" : null;
              const card = (
                <div
                  data-testid={`service-${i}`}
                  className={`relative p-8 md:p-10 transition-colors group h-full ${
                    isFeatured
                      ? "bg-[#E84E34] text-white hover:bg-[#CF412A]"
                      : "bg-white hover:bg-[#0F0F10] hover:text-white"
                  }`}
                >
                  {isFeatured && (
                    <div className="absolute top-4 right-4 font-mono-sm text-[10px] tracking-[0.25em] uppercase bg-white text-[#E84E34] px-2 py-1">
                      DESTAQUE
                    </div>
                  )}
                  <s.icon
                    size={36}
                    strokeWidth={1.5}
                    className={isFeatured ? "text-white" : "text-[#E84E34]"}
                  />
                  <h3 className="mt-6 font-display text-2xl font-bold">{s.title}</h3>
                  <p
                    className={`mt-3 text-sm leading-relaxed ${
                      isFeatured
                        ? "text-white/90"
                        : "text-[#6B7280] group-hover:text-white/70"
                    }`}
                  >
                    {s.desc}
                  </p>
                  {linkTo && (
                    <div className="mt-5 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.18em]">
                      Saber mais <ArrowUpRight size={14} />
                    </div>
                  )}
                </div>
              );
              return linkTo ? (
                <Link key={i} to={linkTo} className="block">
                  {card}
                </Link>
              ) : (
                <div key={i}>{card}</div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRODUTOS DESTAQUE */}
      <section className="py-20 md:py-32 bg-[#F9F9F9]" data-testid="featured-products">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <div className="font-mono-sm text-xs tracking-[0.3em] uppercase text-[#E84E34] mb-4">
                // CATÁLOGO
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight leading-[1.05] max-w-2xl">
                Equipamentos e insumos selecionados.
              </h2>
            </div>
            <Link
              to="/produtos"
              data-testid="featured-see-all"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] hover:text-[#E84E34] transition-colors self-start md:self-auto"
            >
              Ver todos <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* DIFERENCIAIS */}
      <section className="py-20 md:py-32 bg-white" data-testid="differentials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5 relative">
            <img src={AUTOMATION} alt="Automação" className="w-full h-full object-cover" />
            <div className="absolute bottom-0 left-0 bg-[#E84E34] text-white px-6 py-4">
              <div className="font-mono-sm text-[10px] tracking-[0.25em] uppercase">Parceria estratégica</div>
              <div className="font-display text-lg font-bold">+ que fornecedor</div>
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="font-mono-sm text-xs tracking-[0.3em] uppercase text-[#E84E34] mb-4">
              // POR QUE AVANTECH
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight leading-[1.05]">
              Resultado. Otimização. Confiança.
            </h2>
            <p className="mt-6 text-base text-[#374151] leading-relaxed">
              Nosso compromisso é direto: gerar resultado, otimizar operações e ser a empresa que resolve.
            </p>

            <div className="mt-10 grid sm:grid-cols-2 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
              {DIFFERENTIALS.map((d, i) => (
                <div key={i} className="bg-white p-6">
                  <d.icon size={26} strokeWidth={1.5} className="text-[#E84E34]" />
                  <h4 className="mt-4 font-display text-lg font-bold">{d.title}</h4>
                  <p className="mt-1.5 text-sm text-[#6B7280]">{d.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0F0F10] text-white py-20 md:py-28" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="font-mono-sm text-xs tracking-[0.3em] uppercase text-[#E84E34] mb-4">
              // VAMOS CONVERSAR
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight leading-[1.05]">
              Pronto para reduzir custos e aumentar performance?
            </h2>
            <p className="mt-5 text-white/70 text-lg max-w-2xl">
              Fale com nosso time pelo WhatsApp e receba uma proposta sob medida em até 24h úteis.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <a
              href={waLink("Olá Avantech, quero um orçamento sob medida.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-section-whatsapp"
              className="inline-flex items-center gap-3 bg-[#E84E34] text-white font-bold px-7 py-4 uppercase text-xs tracking-[0.2em] hover:bg-[#CF412A] transition-colors w-full md:w-auto justify-center"
            >
              Falar no WhatsApp <ArrowUpRight size={18} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
